/**
 * AddProductDialog — "Create new code" form (v1.17).
 *
 * Mirrors the FieldInsight "Add new Product" modal, pre-filled from the supplier
 * line that triggered it. Used by ResolveCodeDialog's Option B when a supplier
 * code has no matching internal code yet.
 *
 * Key rule: the Supplier field is defaulted to the current line's supplier and
 * rendered READ-ONLY (display only, no search/change). Everything else is
 * editable. On Save the new product is added to the catalog and returned so the
 * caller can set the line's internalCode + persist the supplier-code mapping.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Stack, Box, Typography, MenuItem, InputAdornment, Divider, Chip,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { BRAND, BRAND_HOVER, FI_ORANGE } from '../theme/tokens';
import type { Product } from '../data/types';

export interface AddProductDraft {
  code: string;
  category: string;
  description: string;
  purchasePrice: number;
  markup: number;
  minStock: number;
}

export interface AddProductDialogProps {
  open: boolean;
  /** Supplier this product is being created against (read-only in the form). */
  supplierId: string;
  supplierName: string;
  /** Pre-fill values from the supplier line. */
  prefill: {
    code?: string;        // supplier code -> seeds the Item code
    description?: string;
    purchasePrice?: number;
  };
  /** Existing categories to offer in the picker. */
  categories: string[];
  onClose: () => void;
  /**
   * Called on Save. `addAnother` true -> keep dialog open and reset for the next
   * product. Caller persists the product and returns it (with id).
   */
  onSave: (draft: AddProductDraft, addAnother: boolean) => void;
}

const fieldSx = { '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } };

export const AddProductDialog: React.FC<AddProductDialogProps> = ({
  open, supplierId, supplierName, prefill, categories, onClose, onSave,
}) => {
  const [code, setCode] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('0');
  const [markup, setMarkup] = useState('45');
  const [minStock, setMinStock] = useState('0');

  // Re-seed from prefill whenever the dialog (re)opens.
  useEffect(() => {
    if (!open) return;
    setCode(prefill.code ?? '');
    setCategory('');
    setDescription(prefill.description ?? '');
    setPurchasePrice(prefill.purchasePrice != null ? String(prefill.purchasePrice) : '0');
    setMarkup('45');
    setMinStock('0');
  }, [open, prefill.code, prefill.description, prefill.purchasePrice]);

  const purchaseNum = parseFloat(purchasePrice) || 0;
  const markupNum = parseFloat(markup) || 0;
  const salesPrice = purchaseNum * (1 + markupNum / 100);
  const canSave = code.trim().length > 0 && description.trim().length > 0;

  const buildDraft = (): AddProductDraft => ({
    code: code.trim(),
    category: category.trim() || 'Uncategorised',
    description: description.trim(),
    purchasePrice: purchaseNum,
    markup: markupNum,
    minStock: parseInt(minStock, 10) || 0,
  });

  const handleSave = (addAnother: boolean) => {
    if (!canSave) return;
    onSave(buildDraft(), addAnother);
    if (addAnother) {
      // Reset for the next item; keep supplier + category context.
      setCode('');
      setDescription('');
      setPurchasePrice('0');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '1.05rem' }}>
        <AddBoxIcon sx={{ color: BRAND }} /> Add new Product
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {/* Supplier — read-only context */}
          <Box sx={{ backgroundColor: '#f7f4fc', border: '1px solid #e4d4f5', borderRadius: 1, px: 1.5, py: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
              Supplier
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }}>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: BRAND }}>{supplierName}</Typography>
              <Chip label="Defaulted · not editable" size="small"
                sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#ede7f6', color: BRAND }} />
            </Stack>
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Item code" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              size="small" fullWidth required autoFocus sx={fieldSx}
              helperText="Inventory code"
            />
            <TextField
              label="Category" value={category} onChange={e => setCategory(e.target.value)}
              size="small" fullWidth select sx={fieldSx}
            >
              <MenuItem value=""><em>Uncategorised</em></MenuItem>
              {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Stack>

          <TextField
            label="Description" value={description} onChange={e => setDescription(e.target.value)}
            size="small" fullWidth required multiline minRows={2} sx={fieldSx}
            helperText="Pre-filled from the supplier line — edit as needed"
          />

          <Divider />

          <Stack direction="row" spacing={2}>
            <TextField
              label="Purchase price" value={purchasePrice} onChange={e => setPurchasePrice(e.target.value)}
              size="small" fullWidth type="number" sx={fieldSx}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
            <TextField
              label="Markup %" value={markup} onChange={e => setMarkup(e.target.value)}
              size="small" fullWidth type="number" sx={fieldSx}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <TextField
              label="Sales price" value={salesPrice.toFixed(2)} size="small" fullWidth disabled sx={fieldSx}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Min order qty" value={minStock} onChange={e => setMinStock(e.target.value)}
              size="small" type="number" sx={{ ...fieldSx, width: 160 }}
            />
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#aaa' }}>
                Tax: GST applicable · derived from the line on save
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
        <Button onClick={() => handleSave(true)} disabled={!canSave}
          sx={{ textTransform: 'none', color: FI_ORANGE, fontWeight: 700 }}>
          Save and add another
        </Button>
        <Button onClick={() => handleSave(false)} disabled={!canSave} variant="contained"
          sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: BRAND_HOVER } }}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProductDialog;
